import React, {useContext, useState, useEffect, useRef} from 'react';
import {
  Box,
  Input,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  InputGroup,
  InputRightElement,
  VStack,
  Container,
  Heading,
  useToast,
  Select,
  FormLabel,
  Flex,
  Switch,
  Alert,
  AlertIcon,
  AlertTitle,
  CloseButton,
  Link
} from '@chakra-ui/react';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfigContext } from './utils/Config';
import fetchData from './utils/Util';

function decodeHtmlEntities(str) {
  if (!str) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

const UserInterests = () => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [sortConfig, setSortConfig] = useState({key: 'tree', direction: 'asc'});
  const [filterStatus, setFilterStatus] = useState('all');
  const [hierarchySearch, setHierarchySearch] = useState('');
  const [error, setError] = useState('')
  const toast = useToast();
  const config = useContext(ConfigContext);
  const [boUserID, setBoUserID] = useState(null);
  const searchContainerRef = useRef(null);
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        if (!config.userData || !config.userData.uuid || !config.userData.bo_access_token) {
          return;
        }  
        // Get both sets of data before doing any state updates
        const [statsResponse, clientsResponse] = await Promise.all([
          // Auto categories fetch
          fetchData(
            `${config.siteData.backoffice_url}/api/v1.0/client-stat?` +
            `filter[sale][value]=0` +
            `filter[category][value]=0` +
            `filter[category][operator]=<>` +
            `&client_uuid=${config.userData.uuid}` +
            `&access_token=${config.userData.bo_access_token}` +
            `&fields=id,category,category_tid,weight,bids_sum,winning_bids_sum,winning_bids_count,bids_count,tree,status,browse_url,favorites_count`,
            {mockData: 'api-me'}
          ),
          // Non-auto categories fetch
          fetchData(
            `${config.siteData.backoffice_url}/api/v1.0/datatable-clients?fields=categories,id&filter[uuid][value]=${config.userData.uuid}&access_token=${config.userData.bo_access_token}`,
            {mockData: 'api-me'}
          )
        ]);
  
        if (!statsResponse.ok) throw new Error(statsResponse.title || t('myAccount.networkError'));
        if (!clientsResponse.ok) throw new Error(clientsResponse.title || t('myAccount.networkError'));
  
        const [statsResult, clientsResult] = await Promise.all([
          statsResponse.json(),
          clientsResponse.json()
        ]);

        if (clientsResult.data == null || clientsResult.data[0] == null) {
          return;
        }
  
        setBoUserID(clientsResult.data[0].id);
  
        // Transform auto categories
        const autoCategories = statsResult.data
          .filter(item => item.category_tid)  // Filter out items where category_tid is false/null/undefined
          .map(item => ({
            id: parseInt(item.id),
            cat_id: item.category_tid,
            name: item.category[i18n.language] || item.category.en,
            tree: cleanTreeLabel(item.tree[i18n.language] || item.tree.en),
            browse_url: item.browse_url,
            isSelected: item.status === 'active',
            bidCount: parseInt(item.bids_count) || 0,
            wonCount: parseInt(item.winning_bids_count) || 0,
            weight: item.weight || 0,
            favoriteCount: (parseInt(item.favorites_count) || 0),
            isAuto: 1
          }));
  
        // Create a Set of auto category IDs for quick lookup
        const autoCategoryIds = new Set(autoCategories.map(cat => cat.cat_id.toString()));

        // Only include non-auto categories that don't exist in auto categories
        const nonAutoCategories = clientsResult.data?.[0]?.categories
          ? clientsResult.data[0].categories
            .filter(item => !autoCategoryIds.has(item.id.toString()))  // Convert to string here too
            .map(item => ({
              id: parseInt(item.id),
              cat_id: item.id,
              name: item.label[i18n.language] || item.label.en,
              tree: cleanTreeLabel(item.tree_label[i18n.language] || item.tree_label.en),
              browse_url: item.browse_url,
              isSelected: true,
              bidCount: 0,
              wonCount: 0,
              weight: item.weight || 0,
              favoriteCount: 0,
              isAuto: 0
            }))
          : [];
  
        // Single state update with all categories
        setCategories([...autoCategories, ...nonAutoCategories]);
        setError('');
  
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(error.message);
      }
    };
  
    fetchAllData();
  }, [config.siteData, config.userData, t, i18n.language]);

  // Function to search categories
  const fetchAvailableCategories = async (searchString = '') => {
    if (!config.siteData?.backoffice_url || !config.userData?.bo_access_token) {
      return;
    }

    try {
      const response = await fetch(
          `${config.siteData.backoffice_url}/api/v1.0/categories?` +
          `autocomplete[string]=${encodeURIComponent(searchString)}` +
          `&autocomplete[use_tree_label]=1` +
          `&autocomplete[extended]=1` +
          `&sort=weight`, {
            headers: {
              'access-token': config.userData.bo_access_token,
              'Content-Type': 'application/json',
            }
          });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch categories');
      }

      const result = await response.json();
      // Convert data object into array of categories
      const formattedCategories = Object.entries(result.data).map(([id, categoryData]) => {
        const clientLang = i18n.language;
        return {
          id: parseInt(id),
          name: categoryData[clientLang]?.name || categoryData.en.name,  // Use client language with English fallback
          tree: cleanTreeLabel(categoryData[clientLang]?.tree || categoryData.en.tree),  // Use client language with English fallback
        };
      });

      setError(''); // Clear any previous errors
      setAvailableCategories(formattedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError(error.message || 'Failed to fetch categories');
      setAvailableCategories([]); // Clear results on error
    }
  };

  const cleanTreeLabel = (tree) => {
    if (!tree) return '';
        const pipeIndex = tree.indexOf(' | ');
    if (pipeIndex > 0 && /^\d+/.test(tree)) {
      return tree.substring(pipeIndex + 3);
    }    
    return tree;
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchQuery('');
        setSearchResults([]);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSort = (key) => {
    console.log('Before sort - Current config:', sortConfig);
    setSortConfig((prev) => {
      const newConfig = {
        key, // Use the passed key instead of hardcoding 'tree'
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      };
      console.log('After sort - New config:', newConfig);
      return newConfig;
    });
  };

  const getSortedAndFilteredCategories = () => {
    let filtered = categories.filter(cat => {
      // Status filter
      const statusMatch = filterStatus === 'all' || 
        (filterStatus === 'active' && cat.isSelected) ||
        (filterStatus === 'inactive' && !cat.isSelected);
      
      // Hierarchy search filter
      const searchMatch = !hierarchySearch || 
        decodeHtmlEntities(cat.tree).toLowerCase().includes(hierarchySearch.toLowerCase());
      
      return statusMatch && searchMatch;
    });

    return filtered.sort((a, b) => {
      let compareResult = 0;
      if (sortConfig.key === 'name') {
        const nameA = decodeHtmlEntities(a.name || '');
        const nameB = decodeHtmlEntities(b.name || '');
        compareResult = nameA.localeCompare(nameB);
      } else if (sortConfig.key === 'tree') {
        const weightA = parseInt(a.weight) || 0;
        const weightB = parseInt(b.weight) || 0;
        compareResult = weightA - weightB;
      } else {
        const aValue = parseInt(a[sortConfig.key]) || 0;
        const bValue = parseInt(b[sortConfig.key]) || 0;
        compareResult = aValue - bValue;
      }
      return sortConfig.direction === 'asc' ? compareResult : -compareResult;
    });
  };

  const handleSearch = async (query) => {
    try {
      if (!query.trim()) {
        setSearchResults([]);
        setError('');
        return;
      }

      // First fetch available categories from API
      await fetchAvailableCategories(query);

      // Get opted-out categories with bids
      const optedOutWithBids = categories.filter(cat =>
          !cat.isSelected &&
          cat.bidCount > 0 &&
          (cat.name.toLowerCase().includes(query.toLowerCase()) ||
              cat.tree.toLowerCase().includes(query.toLowerCase()))
      );

      // Get new categories from our fetched results
      const newCategories = availableCategories.filter(category =>
          !categories.some(c => c.id === category.id)
      );

      setSearchResults([
        ...optedOutWithBids,
        ...newCategories
      ]);

    } catch (error) {
      console.error('Error during search:', error);
      setError(error.message || 'Search failed');
      setSearchResults([]);
    }
  };

  const handleAddCategory = (category) => {
    // If it's an existing category that was opted out
    if (categories.some(c => c.id === category.id)) {
      handleToggleCategory(category.id);
      // Remove just this category from search results
      setSearchResults(prev => prev.filter(c => c.id !== category.id));
      return;
    }

    // If it's a new category
    const newCategory = {
      ...category,
      cat_id: category.id,
      tree: cleanTreeLabel(category.tree),
      isSelected: true,
      bidCount: 0,
      wonCount: 0,
      favoriteCount: 0,
      weight: -1,
      // Means is a self added cat.
      isAuto: 0
    };

    // Update phone/ date to formatted values.
    const updatedData = {
      categories: [
        ...categories,
        newCategory
      ].filter(cat => cat.isAuto === 0)
          .map(cat => ({
            id: cat.id,
          }))
    };

    fetch(`${config.siteData.backoffice_url}/api/datatable-clients/${boUserID}?access_token=${config.userData.bo_access_token}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updatedData),
    })
        .then((response) => {
          if (!response.ok) {
            response.json().then((res) => setError(res.title || 'Network error when updating account'));
          } else {
            setCategories(prev => [...prev, newCategory].sort((a, b) => b.weight - a.weight));
            // Remove just this category from search results
            setSearchResults(prev => prev.filter(c => c.id !== category.id));
            toast({
              title: 'Category Added',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }

        })
        .catch((error) => {
          setError(error.message);
        });
  };

  const handleToggleCategory = async (categoryId) => {
    setCategories(prev => {
      const categoryToUpdate = prev.find(cat => cat.id === categoryId);

      if (!categoryToUpdate) return prev;

      // Handle API updates based on category type
      if (categoryToUpdate.isAuto === 1) {
        // Update client-stat API for auto categories
        fetch(`${config.siteData.backoffice_url}/api/v1.0/client-stat/${categoryId}?access_token=${config.userData.bo_access_token}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: categoryToUpdate.isSelected ? 'inactive' : 'active'
          }),
        }).catch(error => {
              console.error('Error updating category status:', error);
              setError(error.message || 'Failed to update category status');
            });
      } else if (categoryToUpdate.isAuto === 0 && categoryToUpdate.isSelected) {
        // Update datatable-clients API for manual categories being deactivated
        const updatedData = {
          categories: prev
              .filter(cat => cat.isAuto === 0 && cat.id !== categoryId)
              .map(cat => ({
                id: cat.id,
              }))
        };

        fetch(`${config.siteData.backoffice_url}/api/datatable-clients/${boUserID}?access_token=${config.userData.bo_access_token}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatedData),
        })
            .catch(error => {
              console.error('Error removing category:', error);
              setError(error.message || 'Failed to remove category');
            });
      }

      const updatedCategories = prev.map(cat => {
        if (cat.id === categoryId) {
          // Always keep the category but update its selected state
          return {...cat, isSelected: !cat.isSelected};
        }
        return cat;
      });

      // Filter out null values (removed categories)
      const filteredCategories = updatedCategories.filter(cat => cat !== null);

      toast({
        title: filteredCategories.length < prev.length ?
            'Category Removed' :
            'Preference Updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      return filteredCategories;
    });
  };

  return (
      <Container maxW="container.xl" py={[3, 6]} px={[2, 4, 6]}>
        <VStack spacing={[3, 6]} align="stretch">
          <Heading size="lg">{t('userInterests.title')}</Heading>

          {error && (
              <Alert status="error">
                <AlertIcon/>
                <AlertTitle>{error}</AlertTitle>
                <CloseButton
                    position="absolute"
                    right="8px"
                    top="8px"
                    onClick={() => setError('')}
                />
              </Alert>
          )}

          <Text fontSize="lg" color="gray.600" mb={6}>
            {t('userInterests.pageDescription')}
          </Text>

          <Box borderWidth="1px" borderRadius="lg" p={[3, 4]} bg="white" shadow="sm">
            <VStack align="stretch" spacing={[3, 4]}>
              <Box>
                <FormLabel fontSize={{ base: "sm", md: "md" }}>{t('userInterests.searchCategories')}</FormLabel>
                <Text mb={[2, 4]} color="gray.600" fontSize={{ base: "sm", md: "md" }}>
                  {t('userInterests.searchInstructions')}
                </Text>
              </Box>
              <Box ref={searchContainerRef} position="relative">
                <InputGroup>
                  <Input
                    value={searchQuery}
                    onChange={async (e) => {
                      setSearchQuery(e.target.value);
                      await handleSearch(e.target.value);
                    }}
                    placeholder={t('userInterests.searchPlaceholder')}
                    pr="4.5rem"
                  />
                  <InputRightElement width="4.5rem">
                    <Flex>
                      {searchQuery && (
                          <Box
                              as="button"
                              onClick={() => {
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              mr="2"
                              _hover={{opacity: 0.7}}
                          >
                            <X size={18}/>
                          </Box>
                      )}
                      <Search/>
                    </Flex>
                  </InputRightElement>
                </InputGroup>

                {/* Move search results outside of InputGroup */}
                {searchQuery && (
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                    position="absolute" 
                    top="100%" 
                    left="0"
                    right="0"
                    zIndex="1000"                     
                    overflowY="auto" 
                    shadow="md" 
                  >
                    {searchResults.length > 0 ? (
                      searchResults.map(category => (
                        <Flex
                          key={category.id}
                          justify="space-between"
                          align="center"
                          py={1.5}
                          px={3}
                          _hover={{bg: 'white'}}
                          borderBottomWidth={1}
                          _last={{borderBottomWidth: 0}}
                          flexDir={{ base: "column", md: "row" }}
                          gap={[2, 0]}
                        >
                          <Text fontSize={{ base: "sm", md: "md" }}>{decodeHtmlEntities(category.tree)}</Text>
                          <Button
                            onClick={() => handleAddCategory(category)}
                            size={{ base: "sm", md: "md" }}
                            colorScheme="blue"
                            w={{ base: "100%", md: "auto" }}
                          >
                            {category.isAuto === 1 ? t('userInterests.reactivate') : t('userInterests.add')}
                          </Button>
                        </Flex>
                      ))
                    ) : (
                      <Text py={2} color="gray.500" textAlign="center">
                        {t('userInterests.noSearchResults')}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            </VStack>
          </Box>

          <Box>
            <Heading size="md" mb={4}>{t('userInterests.myCategories')}</Heading>

            <Flex justify="space-between" mb={4} w="100%" gap={4}>
              <Input
                placeholder="Search hierarchy..."
                onChange={(e) => setHierarchySearch(e.target.value)}
                width={{ base: "100%", md: "300px" }}
                size={{ base: "sm", md: "md" }}
              />
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                width={{ base: "100%", md: "200px" }}
                size={{ base: "sm", md: "md" }}
              >
                <option value="all">Show All Categories</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </Select>
            </Flex>

            <Box overflowX="auto" display={{ base: "none", md: "block" }}>
              <Table variant="simple" size={{ base: "sm", md: "md" }}>
                <Thead>
                  <Tr>
                    <Th>
                      <Box as="a" href="#" onClick={(e) => {
                        e.preventDefault();
                        handleSort('name');
                      }} cursor="pointer" color="inherit" textDecoration="none">
                        {t('userInterests.category')}
                        {sortConfig.key === 'name' && (
                            <Text ml={1} as="span">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Text>
                        )}
                      </Box>
                    </Th>
                    <Th>
                      <Box as="a" href="#" onClick={(e) => {
                        e.preventDefault();
                        handleSort('tree');
                      }} cursor="pointer" color="inherit" textDecoration="none">
                        {t('userInterests.categoryHierarchy')}
                        {sortConfig.key === 'tree' && (
                          <Text ml={1} as="span">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Text>
                        )}
                      </Box>
                    </Th>
                    <Th>
                      <Box as="a" href="#" onClick={(e) => {
                        e.preventDefault();
                        handleSort('bidCount');
                      }} cursor="pointer" color="inherit" textDecoration="none">
                        {t('userInterests.bids')}
                        {sortConfig.key === 'bidCount' && (
                            <Text ml={1} as="span">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Text>
                        )}
                      </Box>
                    </Th>
                    <Th>
                      <Box as="a" href="#" onClick={(e) => {
                        e.preventDefault();
                        handleSort('wonCount');
                      }} cursor="pointer" color="inherit" textDecoration="none">
                        {t('userInterests.won')}
                        {sortConfig.key === 'wonCount' && (
                            <Text ml={1} as="span">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Text>
                        )}
                      </Box>
                    </Th>
                    <Th>
                      <Box as="a" href="#" onClick={(e) => {
                        e.preventDefault();
                        handleSort('favoriteCount');
                      }} cursor="pointer" color="inherit" textDecoration="none">
                        {t('userInterests.favorites')}
                        {sortConfig.key === 'favoriteCount' && (
                            <Text ml={1} as="span">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </Text>
                        )}
                      </Box>
                    </Th>
                    <Th>{t('userInterests.status')}</Th>
                    <Th>{t('userInterests.action')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {getSortedAndFilteredCategories().map(category => (
                      <Tr
                        key={`${category.id}-${category.isAuto ? 'auto' : 'manual'}`}
                      >
                        <Td
                            fontWeight="medium"
                            color={category.isSelected ? "inherit" : "gray.400"}
                        >
                          <Link
                              colorScheme="gray"
                              onClick={() => window.open(category.browse_url, '_blank')}
                              title={t('userInterests.browseCategory')}
                          >
                            {decodeHtmlEntities(category.name)}
                          </Link>
                        </Td>
                        <Td
                            color={category.isSelected ? "gray.600" : "gray.400"}
                        >
                          {decodeHtmlEntities(category.tree)}
                        </Td>
                        <Td
                            isNumeric
                            color={category.isSelected ? "inherit" : "gray.400"}
                        >
                          {category.bidCount > 0 ? category.bidCount : '-'}
                        </Td>
                        <Td
                            isNumeric
                            color={category.isSelected ? "inherit" : "gray.400"}
                        >
                          {category.wonCount > 0 ? category.wonCount : '-'}
                        </Td>
                        <Td
                            isNumeric
                            color={category.isSelected ? "inherit" : "gray.400"}
                        >
                          {category.favoriteCount > 0 ? category.favoriteCount : '-'}
                        </Td>
                        <Td>
                          <Badge
                              colorScheme={category.isSelected ? "green" : "gray"}
                              variant="subtle"
                          >
                            {category.isSelected ? t('userInterests.active') : t('userInterests.inactive')}
                          </Badge>
                        </Td>
                        <Td>
                          <Switch
                              isChecked={category.isSelected}
                              onChange={() => handleToggleCategory(category.id)}
                          />
                        </Td>
                      </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            
            <Box display={{ base: "block", md: "none" }}>
              {getSortedAndFilteredCategories().map(category => (
                <Box 
                  key={`${category.id}-${category.isAuto ? 'auto' : 'manual'}`}
                  p={3}
                  borderWidth="1px"
                  borderRadius="lg"
                  mb={2}
                  bg="white"
                >
                  <VStack align="stretch" spacing={2}>
                    <Flex justify="space-between" align="center">
                      <Link
                        fontWeight="medium"
                        color={category.isSelected ? "inherit" : "gray.400"}
                        onClick={() => window.open(category.browse_url, '_blank')}
                        fontSize="sm"
                      >
                        {decodeHtmlEntities(category.name)}
                      </Link>
                      <Switch
                        size="sm"
                        isChecked={category.isSelected}
                        onChange={() => handleToggleCategory(category.id)}
                      />
                    </Flex>
                    
                    <Text fontSize="xs" color={category.isSelected ? "gray.600" : "gray.400"}>
                      {decodeHtmlEntities(category.tree)}
                    </Text>
                    
                    <Flex justify="space-between" fontSize="xs" mt={1}>
                      <Text>Bids: {category.bidCount > 0 ? category.bidCount : '-'}</Text>
                      <Text>Won: {category.wonCount > 0 ? category.wonCount : '-'}</Text>
                      <Text>Fav: {category.favoriteCount > 0 ? category.favoriteCount : '-'}</Text>
                    </Flex>
                    
                    <Badge
                      colorScheme={category.isSelected ? "green" : "gray"}
                      variant="subtle"
                      alignSelf="flex-start"
                      fontSize="xs"
                    >
                      {category.isSelected ? t('userInterests.active') : t('userInterests.inactive')}
                    </Badge>
                  </VStack>
                </Box>
              ))}
            </Box>


            <Box mt={6}>
              <Text color="gray.600" mb={3}>
                {t('userInterests.browseExplanation')}
              </Text>
              {/*<Button*/}
              {/*    colorScheme="blue"*/}
              {/*    onClick={() => {*/}
              {/*      const selectedCategories = categories*/}
              {/*          .filter(cat => cat.isSelected)*/}
              {/*          .map(cat => cat.cat_id)*/}
              {/*          .join(',');*/}
              {/*      window.open(`${config.siteData.backoffice_url}/search-for/${selectedCategories}`, '_blank');*/}
              {/*    }}*/}
              {/*>*/}
              {/*  {t('userInterests.browseSale')}*/}
              {/*</Button>*/}
            </Box>
          </Box>
        </VStack>
      </Container>
  );
};

export default UserInterests;